import { ConnectionStates, StreamStates } from "@minervaproject/avsubsystem";
import { createSelector } from "@reduxjs/toolkit";

import { selectCurrentUser } from "../global";
import { selectCurrentUserId } from "../local";

import {
  selectActiveBreakout,
  selectActiveBreakoutRoomForCurrentUser,
  selectBreakoutRoomsByBreakoutId,
  selectCurrentUserIsInBreakoutControlRoom,
} from "./breakout";
import { selectUserIdsWithCapability } from "./capability";
import { selectIsInPersonClass } from "./info";

import { selectIsPresentationView } from ".";

import { BreakoutRoomType, CapabilityType, ClassTypeType, RootState } from "shared/types";
import { isChrome } from "utils/browser_detection";

export const selectAllowStudentNoVideo = () => (state: RootState) =>
  state.classroom.client.info.allow_student_no_video;

export const selectEnableStudentAudioOnly = () => (state: RootState) =>
  state.classroom.client.info.enable_student_audio_only;

export const selectCurrentUserCanEnableAudioOnly = () =>
  createSelector(
    [
      selectCurrentUserId(),
      selectUserIdsWithCapability(CapabilityType.CAN_CONTROL_CLASSROOM),
      selectEnableStudentAudioOnly(),
    ],
    (currentUserId, canControlClassroomList, studentCanEnableAudioOnly) => {
      return studentCanEnableAudioOnly || canControlClassroomList.includes(currentUserId);
    },
  );

export const selectAvService = () => (state: RootState) => state.classroom.client.av_service;

export const selectLicodeServer = () => (state: RootState) => state.classroom.client.licode_server;

export const selectPttModeForClass = () => (state: RootState) => state.classroom.client.ptt_mode;

export const selectOpentokSessionId = () => (state: RootState) =>
  state.classroom.client.opentok_session_id;

function _getBreakoutRoomsForUser(breakoutRooms: BreakoutRoomType[], userId: string) {
  let activeBreakoutRoom: BreakoutRoomType | undefined;
  let listeningBreakoutRoom: BreakoutRoomType | undefined;
  breakoutRooms.forEach((breakoutRoom) => {
    if (
      breakoutRoom.assigned_user_ids?.includes(userId) ||
      breakoutRoom.visiting_user_ids?.includes(userId)
    ) {
      activeBreakoutRoom = breakoutRoom;
    }
    if (breakoutRoom.listening_user_ids?.includes(userId)) {
      listeningBreakoutRoom = breakoutRoom;
    }
  });
  return [activeBreakoutRoom, listeningBreakoutRoom];
}

export const selectShouldMuteUserAudio = (userId: string) => (state: RootState) => {
  const currentUser = selectCurrentUser()(state);

  // If the current user is performing an A/V check, they should not hear any audio
  if (currentUser.is_performing_av_check) {
    return [true, "current user is performing an A/V check"];
  }

  // Assistants and professors must not hear each other in a in-person class,
  // but observers must hear all available A/V
  const isInPerson = selectIsInPersonClass()(state);
  const canControlClassroomList = selectUserIdsWithCapability(CapabilityType.CAN_CONTROL_CLASSROOM)(
    state,
  );
  const isVisibleInClassList = selectUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS)(
    state,
  );
  if (
    isInPerson &&
    canControlClassroomList.includes(currentUser.id) &&
    isVisibleInClassList.includes(currentUser.id)
  ) {
    return [true, "starred users must not hear each other in an in-person class"];
  }

  // (For in-person classes, only the instructor is in the participant bar and everybody should hear them)
  const activeBreakout = selectActiveBreakout()(state);
  if (!activeBreakout) {
    return [false, "nobody's audio should be muted in the main classroom"];
  }

  const breakoutRooms = selectBreakoutRoomsByBreakoutId(activeBreakout.id)(state);
  const [userActiveBreakoutRoom, userListeningBreakoutRoom] = _getBreakoutRoomsForUser(
    breakoutRooms,
    userId,
  );
  const [currentUserActiveBreakoutRoom, currentUserListeningBreakoutRoom] =
    _getBreakoutRoomsForUser(breakoutRooms, currentUser.id);

  // If both users are in the same breakout room (control room or regular breakout room) and neither is listening,
  // the current user should hear the other user.
  if (
    currentUserActiveBreakoutRoom === userActiveBreakoutRoom &&
    !currentUserListeningBreakoutRoom &&
    !userListeningBreakoutRoom
  ) {
    return [false, "current user is in the same breakout room"];
  }

  // If the current user is listening to the other user's breakout room, they should hear the other user.
  if (
    currentUserListeningBreakoutRoom &&
    currentUserListeningBreakoutRoom === userActiveBreakoutRoom
  ) {
    return [false, "current user is listening to the other user's breakout room"];
  }

  return [true, "default"];
};

// For in-person classes, we only allow instructors to have AV controls. Note that instructors may be using a mobile
// browser or the presentation view, neither of which should have AV access. Observers will always have AV access.
export const selectCurrentUserIsAvEnabled = () =>
  createSelector(
    [
      selectIsInPersonClass(),
      selectCurrentUserId(),
      selectUserIdsWithCapability(CapabilityType.CAN_CONTROL_CLASSROOM),
      selectIsPresentationView(),
      selectUserIdsWithCapability(CapabilityType.IS_VISIBLE_IN_CLASS),
    ],
    (isInPerson, userId, canControlClassroomList, isPresentationView, isVisibleInClass) => {
      return (
        !isInPerson ||
        (!isVisibleInClass.includes(userId) && isChrome()) ||
        (canControlClassroomList?.includes(userId) && isChrome() && !isPresentationView)
      );
    },
  );

export const selectCurrentUserhasVisibleParticipantAv = () =>
  createSelector(
    [
      selectCurrentUserId(),
      selectActiveBreakoutRoomForCurrentUser(),
      selectParticipantBarUserIds(),
      selectCurrentUserIsInBreakoutControlRoom(),
    ],
    (currentUserId, breakoutRoom, participantBarUserIds, isInBreakoutControlRoom) =>
      participantBarUserIds.includes(currentUserId) || !!breakoutRoom || isInBreakoutControlRoom,
  );

export const selectMaxSubscribedStreams = () => (state: RootState) =>
  state.classroom.client.max_subscribed_streams;

export const selectIsLargeClass = () => (state: RootState) => {
  const isVirtualClass = state.classroom.client.info.type === ClassTypeType.Virtual;
  const presentVisibleUsers = Object.values(state.classroom.client.users).filter(
    (user) =>
      user.is_present && state.classroom.client.capabilities.isVisibleInClass.includes(user.id),
  );
  return (
    isVirtualClass && presentVisibleUsers.length > state.classroom.client.max_subscribed_streams
  );
};

export const selectParticipantBarUserIds = () => (state: RootState) =>
  state.classroom.client.participant_bar_user_ids;

export const selectUserHasAvailableAv = (userId: string) => (state: RootState) => {
  const userHasMicError = !!state.classroom.client.users[userId].av.mic_error;
  const connectionHasFailed =
    state.classroom.client.users[userId].av.connection_state === ConnectionStates.failed;
  const streamHasFailed =
    state.classroom.client.users[userId].av.published_stream_state === StreamStates.failed;
  return !userHasMicError && !connectionHasFailed && !streamHasFailed;
};

export const selectUserHasPoorConnection = (userId: string) => (state: RootState) => {
  const avConnectionQuality = state.classroom.client.users[userId].av.connection_quality;
  const pubsubConnectionQuality = state.classroom.client.users[userId].pubsub_connection_quality;
  // If the av connection quality is null we treat it as poor and follow whatever the pubsub connection quality is
  // This is because we only have avConnection indicator for Licode and not the other AV services
  if (
    avConnectionQuality !== "good" &&
    pubsubConnectionQuality &&
    pubsubConnectionQuality !== "good"
  ) {
    return true;
  }
  return false;
};

export const selectUserCameraError = (userId: string) => (state: RootState) =>
  state.classroom.client.users[userId].av.camera_error;

export const selectUserMicError = (userId: string) => (state: RootState) =>
  state.classroom.client.users[userId].av.mic_error;

export const selectUserPreferredCamera = (userId: string) => (state: RootState) =>
  state.classroom.client.users[userId].pref_camera_device_label;

export const selectUserPreferredMic = (userId: string) => (state: RootState) =>
  state.classroom.client.users[userId].pref_mic_device_label;
